/* eslint-disable max-len */
import swal from "sweetalert";
import * as indexes from "components/studio/Controller.lib.index";
import * as save from "components/studio/Controller.lib.save";
import { toast } from 'react-toastify';
import l from "components/util/l";


/*----------------------------------------------------------------*/
/* Edges                                                          */
/*----------------------------------------------------------------*/

// Function to validate edge connections
const isValidConnection = (source_id, target_id, setNodes) => {

	let source = null;
	let target = null;

	setNodes((nodes) => {
		source = nodes.find((node) => node.id == source_id);
		target = nodes.find((node) => node.id == target_id);
		return nodes;
	})

	if (source.id == target.id) {
		toast(l("Error de conexión: No puedes conectar un nodo consigo mismo%SA007"), { type: "error" });
		return false;
	}

	if (target && target.data?.action?.type == "TRIGGER") {
		toast(l("Error de conexión: Una acción de tipo trigger no puede tener un nodo padre%SA008"), { type: "error" });
		return false;
	}

	return true;
}

// Function when node is connected to another node, see https://reactflow.dev/docs/api/types/#connection
const connectEdges = (source, target, sourceHandle, targetHandle, flowId, activePage, isCasePreview, setNodes, setEdges, updateHistory, callAutoSave) => {

	if (!isValidConnection(source, target, setNodes)) return;

	let mode = "NEXT";
	if (sourceHandle.includes("next")) mode = "NEXT";
	if (sourceHandle.includes("else")) mode = "ELSE";
	if (sourceHandle.includes("end")) mode = "END";

	const newEdge = {
		mode: mode,
		source: source,
		target: target,
		sourceHandle: sourceHandle,
		targetHandle: targetHandle,
		onSave: save.saveBoard
	}

	setEdges((eds) => {
		const newEdges = eds.concat(newEdge)
		setNodes((nodes) => {
			save.saveBoard(nodes, newEdges, flowId, activePage, isCasePreview, updateHistory, callAutoSave);
			return nodes;
		})
		return eds;
	});

};

const updateEdge = (oldEdge, connection, edges, flowId, activePage, isCasePreview, setNodes, setEdges, updateHistory, callAutoSave) => {

	if (!isValidConnection(connection.source, connection.target, setNodes)) return;

	oldEdge = edges.find((edge) => edge.id == oldEdge.id);
	if (!oldEdge) return

	const changedEdge = {
		remoteId: oldEdge.remoteId,
		mode: oldEdge?.mode ?? "NEXT",
		source: connection.source,
		target: connection.target,
		sourceHandle: connection.sourceHandle,
		targetHandle: connection.targetHandle
	}

	setEdges((eds) => {
		let newEdges = [...eds];
		const index = newEdges.findIndex((edge) => edge.id == oldEdge.id);
		if (index == -1) return eds;
		newEdges.splice(index, 1);
		newEdges.push(changedEdge);

		setNodes((nodes) => {
			save.saveBoard(nodes, newEdges, flowId, activePage, isCasePreview, updateHistory, callAutoSave);
			return nodes;
		})

		return eds;
	});

}

const setEdgeFunctions = (edge, flowId, activePage, isCasePreview, updateHistory, callAutoSave) => {
	const newEdge = { ...edge };
	newEdge.data = {};
	newEdge.data.saveBoard = (nodes, edges) => save.saveBoard(nodes, edges, flowId, activePage, isCasePreview, updateHistory, callAutoSave);
	return newEdge;
}

/*----------------------------------------------------------------*/
/* Nodes                                                          */
/*----------------------------------------------------------------*/

const setNodeFunctions = (node, onClickExecuteNode, onClickChangeTriggerStatus, onSelectNode, deleteNode, onClickInsertChild) => {
	const newNode = { ...node };
	newNode.data.executeNode = () => onClickExecuteNode(node.remoteId);
	newNode.data.changeStatusTrigger = () => onClickChangeTriggerStatus(node.id);
	newNode.data.onSelectNode = () => onSelectNode(node.id);
	newNode.data.deleteNode = deleteNode;
	newNode.data.onClickInsertChild = onClickInsertChild;
	return newNode;
}

// Function when node is move to board, see https://reactflow.dev/docs/api/react-flow-props/
const dragNode = (event) => {
	event.preventDefault();
	event.dataTransfer.dropEffect = 'move';
}

// Function when node is dropped on board, see https://reactflow.dev/docs/api/react-flow-props/
const dropNode = (event, reactFlowWrapper, reactFlowInstance, onClickNode, onClickAnnotation) => {

	event.preventDefault();
	const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
	const data = JSON.parse(event.dataTransfer.getData('application/reactflow'));
	const type = data.type;
	const actionId = data?.actionId;

	if (typeof type === 'undefined' || !type) return;
	const position = reactFlowInstance.project({
		x: event.clientX - reactFlowBounds.left,
		y: event.clientY - reactFlowBounds.top
	});

	position.x = parseInt(position.x);
	position.y = parseInt(position.y);

	if (type == "annotation") {
		onClickAnnotation(actionId, position);
	} else if (type == "node") {
		const args = {
			actionId: actionId,
			position: position
		}
		onClickNode(args);
	}

}

const editNode = (remoteId, values, edges, setNodes, flowId, activePage, isCasePreview, updateHistory, callAutoSave) => {
	setNodes((nds) => {
		const actualNode = nds.find((nd) => nd.remoteId == remoteId && nd.isAnnotation == false);
		const resultNodes = nds.filter((nd) => nd.remoteId != remoteId);

		const keys = Object.keys(values);
		const input = keys.map((key) => {
			return { name: key, value: values[key] };
		});

		const editedNode = {
			type: actualNode.type,
			isAnnotation: false,
			remoteId: actualNode.remoteId,
			position: actualNode.position,
			data: {
				action: actualNode.data?.action,
				input_values: input
			}
		}

		const newNodes = resultNodes.concat(editedNode);
		save.saveBoard(newNodes, edges, flowId, activePage, isCasePreview, updateHistory, callAutoSave)
		return nds;
	});
}

const attachNode = (edgeId, actionName, setEdges, setActualNode, setIsNodeFormShown) => {

	let position = { x: 0, y: 0 };
	let edge = null;
	setEdges((eds) => {
		edge = eds.find((edge) => edge.id == edgeId);
		return eds;
	})
	if (!edge) return;

	const source = edge.source
	const target = edge.target;

	let node = {
		type: "node",
		position: position,
		data: { category: actionName },
		isAssistant: false,
		insertBetween: { source: source, target: target }
	};
	setActualNode(node);
	setIsNodeFormShown(true);

}


/*----------------------------------------------------------------*/
/* Annotations                                                    */
/*----------------------------------------------------------------*/

const setAnnotationFunctions = (annotation, onSelectNode, deleteNode) => {
	const newAnnotation = { ...annotation };
	newAnnotation.data.onSelectNode = () => onSelectNode(newAnnotation.id);
	newAnnotation.data.deleteNode = deleteNode;
	return newAnnotation;
}

const editAnnotation = (remoteId, values, edges, flowId, activePageId, isCasePreview, setNodes, updateHistory, callAutoSave) => {

	if (setNodes == null || setNodes == undefined) return;

	setNodes((nds) => {
		const actualNote = nds.find((nd) => nd.remoteId == remoteId && nd.isAnnotation == true);
		const resultNodes = nds.filter((nd) => nd.remoteId != remoteId);

		const keys = Object.keys(values);
		const input = keys.map((key) => {
			return { name: key, value: values[key] };
		});

		actualNote.data.input_values = input;

		const newNodes = resultNodes.concat(actualNote)
		save.saveBoard(newNodes, edges, flowId, activePageId, isCasePreview, updateHistory, callAutoSave);
		return newNodes;
	});

}


/*----------------------------------------------------------------*/
/* General                                                        */
/*----------------------------------------------------------------*/

const getVariables = () => ({
	SCREEN_WIDTH: window.innerWidth,
	SCREEN_HEIGHT: window.innerHeight,
})

const initBoard = (nodes, edges, flowId, activePageId, isCasePreview, updateHistory, onClickExecuteNode,
	onClickChangeTriggerStatus, onClickAttachNode, onSelectNode, deleteNode, onClickInsertChild, callAutoSave) => {

	const newNodes = nodes.filter((item) => item.isAnnotation == false).map((node) => {
		const newNode = setNodeFunctions(node, onClickExecuteNode, onClickChangeTriggerStatus, onSelectNode, deleteNode, onClickInsertChild);
		return newNode;
	});

	const newAnnotations = nodes.filter((item) => item.isAnnotation == true).map((annotation) => {
		const newAnnotation = setAnnotationFunctions(annotation, onSelectNode, deleteNode);
		return newAnnotation;
	})

	const newEdges = edges.map((edge) => {
		edge.data = {
			saveBoard: (newNodes, newEdges) => save.saveBoard(newNodes, newEdges, flowId, activePageId, isCasePreview, updateHistory, callAutoSave),
			attachNode: onClickAttachNode
		};
		return edge;
	});

	const indexedNodes = indexes.assignIdxNodes(newNodes, newEdges);
	return [indexedNodes, newEdges, newAnnotations];

}

const executeNode = (nodes, edges, remoteId, callExecute) => {

	const byId = Object.create(null);
	const byRemote = Object.create(null);
	for (const n of nodes) {
		byId[n.id] = n;
		if (n.remoteId != null) byRemote[n.remoteId] = n;
		if (n.data?.remoteId != null) byRemote[n.data.remoteId] = n;
	}

	const root = byRemote[remoteId];
	if (!root) return false;

	const adj = Object.create(null);
	for (const n of nodes) adj[n.id] = [];
	for (const e of edges) {
		if (byId[e.source] && byId[e.target]) adj[e.source].push(e.target);
	}

	const findFirstIssue = (startId) => {
		const inStack = new Set();
		const visited = new Set();

		const stack = [{ id: startId, entering: true }];

		while (stack.length) {
			const { id, entering } = stack.pop();

			if (entering) {

				inStack.add(id);
				const n = byId[id];
				if (n) {
					const errors = n.data?.errors ?? [];
					const warnings = n.data?.warnings ?? [];
					if (errors.length) return { node: n, reason: "error" };
					if (warnings.length) return { node: n, reason: "warning" };

					stack.push({ id, entering: false });
					const children = adj[id] || [];
					for (let i = children.length - 1; i >= 0; i--) {
						const ch = children[i];
						if (!visited.has(ch)) stack.push({ id: ch, entering: true });
					}
				} else {
					inStack.delete(id);
					visited.add(id);
				}
			} else {
				inStack.delete(id);
				visited.add(id);
			}
		}
		return null;
	};

	const issue = findFirstIssue(root.id);

	const doExecute = () => {
		callExecute({
			root_node_id: remoteId,
			session_id: sessionStorage.getItem("execution_session_id"),
			user_id: sessionStorage.getItem("id"),
			variables: getVariables(),
		});
	};

	if (!issue) {
		// No hay errores, advertencias ni ciclos
		return doExecute();
	}


	const node = issue.node;
	const action = node?.data?.action;
	const uiSettings = action?.uiSettings;
	const actionTitle = uiSettings?.node?.title ?? uiSettings?.general?.title ?? l("Nodo");

	swal({
		title: l("Su proceso cuenta con observaciones%SA011"),
		icon: "warning",
		text:
			`${l("Se encontró un problema en")} ${actionTitle}[${node.id}].\n\n` +
			l("¿Estás seguro de ejecutar el proceso de todos modos?%SA012"),
		buttons: {
			confirm: {
				text: l("Aceptar%GA020"),
				className: "swal-button swal-button--cancel btn-success",
			},
			cancel: l("Cancelar%GA021"),
		},
	}).then((response) => {
		if (response) doExecute();
	});

}


export { initBoard, executeNode, setNodeFunctions, setEdgeFunctions, setAnnotationFunctions, connectEdges, updateEdge, dragNode, dropNode, editNode, attachNode, editAnnotation };